<script lang="ts">
  import { onMount } from 'svelte';
  import { goto, route } from '@mateothegreat/svelte5-router';
  import { navigation } from '../stores/navigation';
  import { environment } from '../stores/environment';

  let currentPath = $state('/');
  let isReaderPage = $state(false);
  let isMobile = $state(true);

  // Window size detection for adaptive layout (Material Design 3 breakpoints)
  // Compact: < 600px (mobile) - Bottom Navigation Bar
  // Medium/Expanded: >= 600px (tablet/desktop) - Navigation Rail
  const MOBILE_BREAKPOINT = 600;

  onMount(() => {
    // Subscribe to route changes via History API events
    const updateRoute = () => {
      currentPath = window.location.pathname + window.location.search + window.location.hash.replace(/^#\/?/, '/');
      isReaderPage = currentPath.startsWith('/reader');
      navigation.setRoute(currentPath);
    };
    updateRoute();
    window.addEventListener('pushState', updateRoute);
    window.addEventListener('replaceState', updateRoute);
    window.addEventListener('popstate', updateRoute);
    window.addEventListener('hashchange', updateRoute);

    // Handle window resize for adaptive layout
    function handleResize() {
      isMobile = window.innerWidth < MOBILE_BREAKPOINT;
    }

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('pushState', updateRoute);
      window.removeEventListener('replaceState', updateRoute);
      window.removeEventListener('popstate', updateRoute);
      window.removeEventListener('hashchange', updateRoute);
      window.removeEventListener('resize', handleResize);
    };
  });

  interface NavItem {
    path: string;
    label: string;
    icon: string;
  }

  const navItems: NavItem[] = [
    {
      path: '/',
      label: 'Library',
      icon: 'library',
    },
    {
      path: '/reader',
      label: 'Reader',
      icon: 'book',
    },
    {
      path: '/settings',
      label: 'Settings',
      icon: 'settings',
    },
    {
      path: '/profile',
      label: 'Profile',
      icon: 'person',
    },
  ];

  function getNavigationPath(item: NavItem): string {
    if (item.path === '/reader') {
      const lastReaderRoute = navigation.getLastReaderRoute();
      return lastReaderRoute || '/reader';
    }
    return item.path;
  }

  function isActive(path: string): boolean {
    if (path === '/') {
      return currentPath === '/';
    }
    return currentPath.startsWith(path);
  }

  function getIcon(icon: string): string {
    const icons: Record<string, string> = {
      library: '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" />',
      book: '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />',
      settings: '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />',
      person: '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />',
    };
    return icons[icon] || '';
  }
</script>

{#if isMobile}
  <!-- Mobile: Bottom Navigation Bar -->
  <!-- Hidden on reader page to maximize reading area -->
  {#if !isReaderPage}
    <nav class="fixed bottom-0 left-0 right-0 bg-white dark:bg-gray-800 border-t dark:border-gray-700 z-50 safe-area-bottom">
      <div class="flex justify-around items-center h-12">
        {#each navItems as item}
          <a
            href={getNavigationPath(item)}
            use:route
            class="flex flex-col items-center justify-center w-full h-full px-2 transition-colors {isActive(item.path) ? 'text-blue-600 dark:text-blue-400' : 'text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100'}"
            aria-label={item.label}
          >
            <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {@html getIcon(item.icon)}
            </svg>
            <span class="text-xs mt-1 {isActive(item.path) ? 'font-medium' : ''}">{item.label}</span>
          </a>
        {/each}
      </div>
    </nav>
  {/if}
{:else}
  <!-- Desktop/Tablet: Navigation Rail (Side) -->
  <nav class="w-20 bg-white dark:bg-gray-800 border-r dark:border-gray-700 flex-shrink-0 flex flex-col items-center py-4">
    <!-- App Logo/Icon
    <a href="/" use:route class="mb-8 p-3 rounded-xl hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
      <svg class="w-8 h-8 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
      </svg>
    </a> -->

    <!-- Navigation Items -->
    <div class="flex-1 flex flex-col space-y-2 w-full px-2">
      {#each navItems as item}
        <a
          href={getNavigationPath(item)}
          use:route
          class="flex flex-col items-center justify-center py-4 rounded-xl transition-colors {isActive(item.path) ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-gray-900 dark:hover:text-gray-100'}"
          aria-label={item.label}
          title={item.label}
        >
          <svg class="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            {@html getIcon(item.icon)}
          </svg>
          <span class="text-xs mt-1 {isActive(item.path) ? 'font-medium' : ''}">{item.label}</span>
        </a>
      {/each}
    </div>
  </nav>
{/if}

<style>
  /* Safe area support for devices with notches */
  .safe-area-bottom {
    padding-bottom: env(safe-area-inset-bottom);
  }
</style>
